import { z } from 'zod';
import type { Flag } from '../commands/envelope.js';
import { type FefoContext, type FefoLot, allocateFefo, compareFefo } from '../fefo/fefo.js';
import { D } from '../internal/decimal.js';
import { type Cents, cents } from '../money/index.js';
import { type Qty, type QtyInput, qty, qtyToString } from '../quantity/index.js';
import { type Instant, type PlainDate, addDays, localDate } from '../time/index.js';

/**
 * Production batches (plan §5.4, §9.1, §9.5; vault Core Model "the trinity" and "batch
 * output allocation"): the batch lifecycle, where completion's components come from
 * (FEFO, or the lots a baker scanned), the output lot's best-before, yield variance and
 * actual cost. Pure functions: the server applies them inside the completion transaction,
 * and a station runs the same code to show the effect offline.
 * @module
 */

/** Batch statuses: `planned → started → completed`, or `cancelled` before completion. */
export const BATCH_STATUSES = ['planned', 'started', 'completed', 'cancelled'] as const;
/** One of {@link BATCH_STATUSES}. */
export type BatchStatus = (typeof BATCH_STATUSES)[number];
/** zod schema for a batch status. */
export const batchStatusSchema = z.enum(BATCH_STATUSES);

/** Transitions a batch goes through exactly once each (`batch_state_transition`). */
export const BATCH_TRANSITIONS = ['start', 'complete', 'cancel'] as const;
/** One of {@link BATCH_TRANSITIONS}. */
export type BatchTransition = (typeof BATCH_TRANSITIONS)[number];

/** Where a batch's output goes (vault "destination-tagged production"). */
export const BATCH_DESTINATIONS = [
  'shelf',
  'order_claim',
  'branch_transfer',
  'immediate_order',
] as const;
/** One of {@link BATCH_DESTINATIONS}. */
export type BatchDestination = (typeof BATCH_DESTINATIONS)[number];
/** zod schema for a batch destination. */
export const batchDestinationSchema = z.enum(BATCH_DESTINATIONS);

/**
 * The status after `transition`, or why it can't happen. Completion is a **physical** fact:
 * it applies even to a cancelled batch (the goods exist), flagged
 * `completed_after_cancel` for a person; a second completion is `already_completed`.
 */
export function batchTransition(
  status: BatchStatus,
  transition: BatchTransition,
):
  | { ok: true; status: BatchStatus; flag?: 'completed_after_cancel' }
  | { ok: false; reason: 'already_started' | 'already_completed' | 'already_cancelled' } {
  switch (transition) {
    case 'start':
      if (status === 'planned') return { ok: true, status: 'started' };
      if (status === 'started') return { ok: false, reason: 'already_started' };
      return {
        ok: false,
        reason: status === 'completed' ? 'already_completed' : 'already_cancelled',
      };
    case 'complete':
      if (status === 'completed') return { ok: false, reason: 'already_completed' };
      if (status === 'cancelled') {
        return { ok: true, status: 'completed', flag: 'completed_after_cancel' };
      }
      return { ok: true, status: 'completed' };
    case 'cancel':
      if (status === 'planned' || status === 'started') return { ok: true, status: 'cancelled' };
      return {
        ok: false,
        reason: status === 'completed' ? 'already_completed' : 'already_cancelled',
      };
  }
}

// ---------------------------------------------------------------------------------------
// Output lot

/** Result of {@link outputBestBefore}. */
export interface OutputBestBefore {
  /** Local calendar date (the account's zone); `null` when the product has none. */
  bestBefore: PlainDate | null;
  /** The product tracks expiry but has no shelf life: a person must add one. */
  missingShelfLife: boolean;
}

/**
 * Best-before of a batch's output: the **local** (account zone, SAST) date the batch was
 * completed + the product's shelf life. A product that tracks expiry (or has a state rule)
 * but has no shelf life gets none, and says so (`missingShelfLife`) — never a guessed date.
 */
export function outputBestBefore(p: {
  completedAt: Instant;
  timeZone: string;
  shelfLifeDays: number | null;
  tracksExpiry: boolean;
  hasStateRule: boolean;
}): OutputBestBefore {
  const tracks = p.tracksExpiry || p.hasStateRule || p.shelfLifeDays !== null;
  if (!tracks) return { bestBefore: null, missingShelfLife: false };
  if (p.shelfLifeDays === null) return { bestBefore: null, missingShelfLife: true };
  return {
    bestBefore: addDays(localDate(p.completedAt, p.timeZone), p.shelfLifeDays),
    missingShelfLife: false,
  };
}

// ---------------------------------------------------------------------------------------
// Consumption at completion

/** A component the batch needs, in its base unit (from `explodeRecipe`). */
export interface ComponentNeed {
  componentId: string;
  qty: QtyInput;
  allowedStates: readonly string[];
}

/** A lot the baker scanned for a component: it replaces FEFO for that component. */
export interface ScannedLot {
  productId: string;
  lotId: string;
  locationId: string;
  /** The row's state; default: the lot's state at the location with the most stock. */
  state?: string;
  /**
   * How much of it was used (base unit). Default: what the given quantities leave of the
   * need, spread in scan order.
   */
  qty?: string;
}

/** One row consumption takes from (a ledger movement of −qty). */
export interface ConsumptionLine {
  componentId: string;
  lotId: string | null;
  locationId: string;
  state: string;
  /** Positive, in the component's base unit. */
  qty: string;
  /** `fefo`: FEFO chose it; `scanned`: the baker scanned it; `beyond_stock`: more than the books had. */
  source: 'fefo' | 'scanned' | 'beyond_stock';
}

/** Per component: what the recipe needs vs what is taken. */
export interface ComponentUsage {
  componentId: string;
  theoretical: string;
  consumed: string;
  /** Taken beyond recorded stock (the ledger goes negative there). */
  beyondStock: string;
}

/** Result of {@link planBatchConsumption}. */
export interface ConsumptionPlan {
  lines: ConsumptionLine[];
  usage: ComponentUsage[];
  /**
   * `component_shortage` (less usable stock than needed: consumed anyway, beyond stock),
   * `expires_before_use` (stock existed but expires before the batch date), `over_reserved`,
   * `scanned_lot_short` (a scanned lot had less on the books than was used),
   * `reservation_broken` (stock claimed for an order was used: FEFO takes it last). Never a
   * rejection: completing a batch is a physical fact.
   */
  flags: Flag[];
}

/** What {@link planBatchConsumption} needs to know about each component. */
export interface ConsumptionComponent {
  /** FEFO context: zone, base unit, state rule, which locations feed production. */
  context: FefoContext;
  /** Its first state (for stock that has to go negative where none is recorded). */
  firstState: string;
}

const s = (d: InstanceType<typeof D>) => qtyToString(qty(d));
const positive = (d: InstanceType<typeof D>) => d.isPositive() && !d.isZero();

/**
 * Plans what a batch's completion consumes (plan §5.6 step 2 rules, applied for real at
 * completion): per component, the scanned lots when any were scanned for it, else **FEFO**
 * over `stock` (production-feeding locations, a state usable as an ingredient and allowed
 * by the line, best-before on or after the local date of `useAt`); stock with a reservation
 * on it (claimed for an order) only after every unclaimed row, flagged `reservation_broken`
 * (D-065). What usable stock can't cover is still consumed — from the FEFO-first row at a
 * production-feeding location, or lot-less at `fallbackLocationId` — and flagged
 * `component_shortage` (physical facts are never rejected; plan §9.3, §9.5).
 */
export function planBatchConsumption(input: {
  needs: readonly ComponentNeed[];
  stock: readonly FefoLot[];
  components: ReadonlyMap<string, ConsumptionComponent>;
  useAt: Instant;
  scanned?: readonly ScannedLot[];
  fallbackLocationId: string;
}): ConsumptionPlan {
  const lines: ConsumptionLine[] = [];
  const flags: Flag[] = [];
  // Rows as they stand while this plan draws from them (a component can appear twice).
  const available = new Map<string, InstanceType<typeof D>>();
  const rowKey = (r: { lotId: string | null; locationId: string; state: string }) =>
    `${r.lotId ?? ''}|${r.locationId}|${r.state}`;
  for (const r of input.stock) {
    const k = `${r.productId}|${rowKey({ ...r, lotId: r.lotId === '' ? null : r.lotId })}`;
    available.set(k, (available.get(k) ?? new D(0)).plus(qty(r.qty)));
  }
  const take = (
    componentId: string,
    row: Omit<ConsumptionLine, 'componentId' | 'qty' | 'source'>,
    amount: InstanceType<typeof D>,
    source: ConsumptionLine['source'],
  ) => {
    const k = `${componentId}|${rowKey(row)}`;
    available.set(k, (available.get(k) ?? new D(0)).minus(amount));
    const last = lines.find(
      (l) => l.componentId === componentId && rowKey(l) === rowKey(row) && l.source === source,
    );
    if (last) last.qty = s(qty(last.qty).plus(amount));
    else lines.push({ componentId, ...row, qty: s(amount), source });
  };
  const rowsOf = (componentId: string): FefoLot[] =>
    input.stock
      .filter((r) => r.productId === componentId)
      .map((r) => {
        const k = `${componentId}|${rowKey({ ...r, lotId: r.lotId === '' ? null : r.lotId })}`;
        return { ...r, qty: s(available.get(k) ?? new D(0)) };
      });

  const usage = new Map<string, ComponentUsage>();
  for (const need of input.needs) {
    const amount = qty(need.qty);
    const u = usage.get(need.componentId) ?? {
      componentId: need.componentId,
      theoretical: '0',
      consumed: '0',
      beyondStock: '0',
    };
    usage.set(need.componentId, u);
    u.theoretical = s(qty(u.theoretical).plus(amount));
    if (!positive(amount)) continue;
    const component = input.components.get(need.componentId);
    const scans = (input.scanned ?? []).filter((x) => x.productId === need.componentId);
    const consumedBefore = lines
      .filter((l) => l.componentId === need.componentId)
      .reduce((t, l) => t.plus(qty(l.qty)), new D(0));

    if (scans.length > 0) {
      planScanned(need.componentId, amount, scans, component, rowsOf, take, flags);
    } else {
      planFefo(need, amount, component, rowsOf, take, flags, input);
    }
    const consumedNow = lines
      .filter((l) => l.componentId === need.componentId)
      .reduce((t, l) => t.plus(qty(l.qty)), new D(0));
    u.consumed = s(qty(u.consumed).plus(consumedNow.minus(consumedBefore)));
  }
  for (const l of lines) {
    if (l.source !== 'beyond_stock') continue;
    const u = usage.get(l.componentId) as ComponentUsage;
    u.beyondStock = s(qty(u.beyondStock).plus(qty(l.qty)));
  }
  return { lines, usage: [...usage.values()], flags };
}

type Take = (
  componentId: string,
  row: Omit<ConsumptionLine, 'componentId' | 'qty' | 'source'>,
  amount: InstanceType<typeof D>,
  source: ConsumptionLine['source'],
) => void;

function planScanned(
  componentId: string,
  need: InstanceType<typeof D>,
  scans: readonly ScannedLot[],
  component: ConsumptionComponent | undefined,
  rowsOf: (id: string) => FefoLot[],
  take: Take,
  flags: Flag[],
): void {
  const rows = rowsOf(componentId);
  const stateOf = (scan: ScannedLot): string => {
    if (scan.state !== undefined) return scan.state;
    const here = rows
      .filter((r) => r.lotId === scan.lotId && r.locationId === scan.locationId)
      .sort((a, b) => qty(b.qty).comparedTo(qty(a.qty)) || (a.state < b.state ? -1 : 1));
    return here[0]?.state ?? component?.firstState ?? 'default';
  };
  const onBooks = (scan: ScannedLot, state: string) =>
    rows
      .filter(
        (r) => r.lotId === scan.lotId && r.locationId === scan.locationId && r.state === state,
      )
      .reduce((t, r) => t.plus(qty(r.qty)), new D(0));

  // A quantity the baker gave is what was used from that lot (CR5-13: even when other scans
  // of the component have none). The rest of the need is spread over the scans without one,
  // in scan order: each up to what the books hold, the last one takes whatever is left.
  const given = scans.reduce((t, x) => (x.qty === undefined ? t : t.plus(qty(x.qty))), new D(0));
  let remaining = D.max(need.minus(given), new D(0));
  const lastSpread = scans.reduce((last, x, i) => (x.qty === undefined ? i : last), -1);
  scans.forEach((scan, i) => {
    const state = stateOf(scan);
    const row = { lotId: scan.lotId, locationId: scan.locationId, state };
    const books = onBooks(scan, state);
    let amount: InstanceType<typeof D>;
    if (scan.qty !== undefined) amount = qty(scan.qty);
    else if (i === lastSpread) amount = remaining;
    else amount = D.min(remaining, D.max(books, new D(0)));
    if (!positive(amount)) return;
    if (scan.qty === undefined) remaining = D.max(remaining.minus(amount), new D(0));
    const covered = D.min(amount, D.max(books, new D(0)));
    if (positive(covered)) take(componentId, row, covered, 'scanned');
    const beyond = amount.minus(covered);
    if (positive(beyond)) {
      take(componentId, row, beyond, 'beyond_stock');
      flags.push({
        code: 'scanned_lot_short',
        message: 'A scanned lot had less on the books than was used; it goes below zero.',
        data: {
          product_id: componentId,
          lot_id: scan.lotId,
          location_id: scan.locationId,
          state,
          on_books: s(books),
          used: s(amount),
        },
      });
    }
  });
}

const sameRow = (a: FefoLot, b: FefoLot) =>
  a.lotId === b.lotId && a.locationId === b.locationId && a.state === b.state;

function planFefo(
  need: ComponentNeed,
  amount: Qty,
  component: ConsumptionComponent | undefined,
  rowsOf: (id: string) => FefoLot[],
  take: Take,
  flags: Flag[],
  input: { useAt: Instant; fallbackLocationId: string },
): void {
  const all = rowsOf(need.componentId);
  const eligible =
    need.allowedStates.length > 0 ? all.filter((r) => need.allowedStates.includes(r.state)) : all;
  let allocated = new D(0);
  if (component) {
    const request = {
      productId: need.componentId,
      uom: component.context.baseUom,
      purpose: 'consumption' as const,
      useAt: input.useAt,
    };
    const takeAll = (result: ReturnType<typeof allocateFefo>) => {
      for (const a of result.allocations) {
        take(
          need.componentId,
          { lotId: a.lotId === '' ? null : a.lotId, locationId: a.locationId, state: a.state },
          qty(a.qty),
          'fefo',
        );
      }
      allocated = allocated.plus(qty(result.allocated));
    };
    // First the stock nobody has claimed (reservations are held back)…
    const free = allocateFefo(
      { ...request, qty: qtyToString(amount) },
      eligible,
      component.context,
    );
    takeAll(free);
    // …then, only if that is not enough, stock claimed for an order (D-065, CR5-7): the
    // goods went into the batch, so the claim is broken — flagged for a person, never hidden.
    const short = amount.minus(allocated);
    const claimed = rowsOf(need.componentId).filter(
      (r) => (r.reservations ?? []).length > 0 && eligible.some((e) => sameRow(e, r)),
    );
    if (positive(short) && claimed.length > 0) {
      const second = allocateFefo(
        { ...request, qty: s(short) },
        claimed.map((r) => ({ ...r, reservations: [] })),
        component.context,
      );
      takeAll(second);
      for (const a of second.allocations) {
        const row = claimed.find(
          (r) => r.lotId === a.lotId && r.locationId === a.locationId && r.state === a.state,
        ) as FefoLot;
        const reservations = row.reservations ?? [];
        flags.push({
          code: 'reservation_broken',
          message:
            'Stock claimed for a customer order was used in this batch: the order’s claim is short.',
          data: {
            product_id: need.componentId,
            lot_id: a.lotId === '' ? null : a.lotId,
            location_id: a.locationId,
            state: a.state,
            qty: a.qty,
            reservation_ids: reservations.map((r) => r.reservationId),
            order_line_ids: [
              ...new Set(reservations.flatMap((r) => (r.orderLineId ? [r.orderLineId] : []))),
            ],
          },
        });
      }
    }
    for (const f of free.flags) {
      if (f.code === 'shortfall' || f.code === 'expires_before_use') continue;
      flags.push({ ...f, data: { ...f.data, product_id: need.componentId } });
    }
    // `expires_before_use` explains a shortage: only when stock still ran short at the end.
    for (const f of positive(amount.minus(allocated)) ? free.flags : []) {
      if (f.code !== 'expires_before_use') continue;
      flags.push({ ...f, data: { ...f.data, product_id: need.componentId } });
    }
  }
  const missing = amount.minus(allocated);
  if (!positive(missing)) return;
  // Never onto a row whose recorded state is blocked for production (D-068: stock booked
  // in its expired state stays out of every consumption, shortfalls included).
  const blocked = (state: string) =>
    component?.context.stateRule.find((st) => st.key === state)?.usableAsIngredient === false;
  const feeding = all
    .filter(
      (r) => component?.context.locations.get(r.locationId)?.feedsProduction && !blocked(r.state),
    )
    .sort(compareFefo)[0];
  const target = feeding
    ? {
        lotId: feeding.lotId === '' ? null : feeding.lotId,
        locationId: feeding.locationId,
        state: feeding.state,
      }
    : {
        lotId: null,
        locationId: input.fallbackLocationId,
        state: component?.firstState ?? 'default',
      };
  take(need.componentId, target, missing, 'beyond_stock');
  flags.push({
    code: 'component_shortage',
    message: 'Less usable stock was recorded than the batch used; consumed anyway, below zero.',
    data: {
      product_id: need.componentId,
      needed: qtyToString(amount),
      from_stock: s(allocated),
      missing: s(missing),
    },
  });
}

// ---------------------------------------------------------------------------------------
// Yield and cost

/** A variance against a base: `qty` (actual − base) and percent of the base. */
export interface Variance {
  qty: string;
  /** 2 decimals; `null` when the base is 0. */
  pct: string | null;
}

/** Yield variance of a completed batch (vault: "tracked and reported; never changes prices"). */
export interface YieldVariance {
  /** Actual yield vs the planned quantity. */
  vsPlanned: Variance;
  /** Actual yield vs what the recipe yields for the inputs used (`inputQty`). */
  vsRecipe: Variance;
}

function variance(actual: InstanceType<typeof D>, base: InstanceType<typeof D>): Variance {
  const diff = actual.minus(base);
  return {
    qty: s(diff),
    pct: base.isZero()
      ? null
      : diff.div(base).times(100).toDecimalPlaces(2, D.ROUND_HALF_UP).toFixed(2),
  };
}

/**
 * Yield variance: actual vs planned, and actual vs the recipe's yield for what was put in
 * (`inputQty`: the quantity the recipe was scaled for — the planned quantity unless the
 * baker said otherwise).
 */
export function yieldVariance(p: {
  plannedQty: QtyInput;
  inputQty: QtyInput;
  actualQty: QtyInput;
}): YieldVariance {
  const actual = qty(p.actualQty);
  return {
    vsPlanned: variance(actual, qty(p.plannedQty)),
    vsRecipe: variance(actual, qty(p.inputQty)),
  };
}

/** Actual cost of a completed batch (vault "Costing: standard vs actual"). */
export interface BatchActualCost {
  /** Σ consumed qty × unit cost, whole cents; `null` when no component had a cost. */
  totalCents: Cents | null;
  /** Per unit of actual yield; `null` without a total or with no yield. */
  unitCents: Cents | null;
  /** Every consumed component had a cost. */
  complete: boolean;
  /** Components consumed without a cost. */
  missing: string[];
}

/**
 * Actual cost: what the batch consumed, at each component's cost per base unit (`unitCost`,
 * exact decimal string or `null`). Lots carry no purchase cost until receiving (M6), so the
 * caller passes the component's standard cost — the basis is recorded with the batch.
 */
export function batchActualCost(
  consumed: readonly { componentId: string; qty: QtyInput }[],
  unitCost: (componentId: string) => string | null,
  actualYield: QtyInput,
): BatchActualCost {
  let total = new D(0);
  let any = false;
  const missing = new Set<string>();
  for (const c of consumed) {
    const cost = unitCost(c.componentId);
    if (cost === null) {
      if (!qty(c.qty).isZero()) missing.add(c.componentId);
      continue;
    }
    any = true;
    total = total.plus(new D(cost).times(qty(c.qty)));
  }
  const totalCents = any ? cents(total.toDecimalPlaces(0, D.ROUND_HALF_UP).toNumber()) : null;
  const y = qty(actualYield);
  const unitCents =
    totalCents === null || !positive(y)
      ? null
      : cents(total.div(y).toDecimalPlaces(0, D.ROUND_HALF_UP).toNumber());
  return { totalCents, unitCents, complete: missing.size === 0, missing: [...missing].sort() };
}
